import {
  usePartnerMessage,
  usePartnerMessageFunction,
  usePartnerMessages,
  usePartnerResourceString,
  usePartnerResourceStringOrDefault,
  usePartnerResourceStrings,
} from "@gamedev-web/gamedev-sdk-core";

const BaseStringsId = "baseStrings";

/**
 * A react hook that gets a given resource string from the ResourceStringContext and formats with given params accordingly
 * @param {string} key The key for the resource string
 * @param {string[]} args The optional list of arguments to be formatted in the resource string
 * @returns {string} The resource string from the key formatted as appropriate
 */
export const useResourceString = (key: string, ...args: string[]): string =>
  usePartnerResourceString<string>(key, BaseStringsId, ...args);

/**
 * A react hook that gets a list of resource strings from the ResourceStringContext
 * Does not handle formatting. To format corresponding strings, use @gamedev-web/platform-sdk/format
 * @example
 *  const [ string1, string2, string3 ] = useResourceStrings(ResourceStrings.string1, ResourceStrings.string2, ResourceString.string3);
 * @param {string[]} keys A list of keys to get resource strings for
 * @returns {string[]} The list of resource strings as per requested
 */
export const useResourceStrings = (...keys: string[]): string[] =>
  usePartnerResourceStrings<string>(BaseStringsId, ...keys);

/**
 * Like useResourceString, but instead of logging an error when a string is missing
 * it will fall back to the default string. Useful for situations where a key is dynamic
 * and there may or may not be a translation for it.
 */
export const useResourceStringOrDefault = (
  key: string,
  defaultString: string | undefined,
  ...args: string[]
) => usePartnerResourceStringOrDefault(BaseStringsId, key, defaultString, ...args);

type MessageParams = Record<string, string | number> | string[];

export function useMessage(key: string, params?: MessageParams) {
  return usePartnerMessage<string>(BaseStringsId, key, params);
}

export function useMessages(...keys: string[]) {
  return usePartnerMessages<string>(BaseStringsId, ...keys);
}

export function useMessageFunction(key: string) {
  return usePartnerMessageFunction<string>(BaseStringsId, key);
}
